"use client";
import { CheckMarkIcon2 } from "@/assets/icons";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { ChevronDown } from "../Header/icons";
import { VendureFacet } from "@/lib/vendure/models";

type PropsType = {
  facet: VendureFacet;
  facetsCount: Record<string, Record<string, number>>;
};

const FacetDropdown = ({ facet, facetsCount }: PropsType) => {
  const [isOpen, setIsOpen] = useState(true);

  const router = useRouter();
  const searchParams = useSearchParams() || new URLSearchParams();
  const pathname = usePathname();

  const handleFacet = (
    facetCode: string,
    valueCode: string,
    isChecked: boolean,
  ) => {
    const params = new URLSearchParams(searchParams);
    const categoryParam = params.get(facetCode);

    if (isChecked) {
      params.set(
        facetCode,
        categoryParam ? `${categoryParam},${valueCode}` : valueCode,
      );
    } else {
      const newParam = categoryParam
        ?.split(",")
        .filter((id) => id !== valueCode);

      if (newParam?.length) params.set(facetCode, newParam.join(","));
      else params.delete(facetCode);
    }

    params.set("page", "1");

    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: "smooth" }),
    );
  };

  if (!facet.values.length) return null;

  return (
    <div className="bg-white rounded-lg shadow-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`cursor-pointer flex items-center justify-between py-3 pl-6 pr-5.5 w-full ${
          isOpen && "shadow-filter"
        }`}
      >
        <span className="text-dark">{facet.name}</span>

        <ChevronDown
          className={`text-dark ease-out duration-200 ${isOpen && "rotate-180"}`}
        />
      </button>

      <div className="flex flex-col gap-3 py-6 pl-6 pr-5.5" hidden={!isOpen}>
        {facet.values.map((value) => (
          <label
            htmlFor={value.code}
            key={value.code}
            className="flex items-center justify-start gap-2 cursor-pointer group hover:text-blue"
          >
            <input
              type="checkbox"
              className="sr-only peer"
              defaultChecked={searchParams
                .get(facet.code)
                ?.split(",")
                .includes(value.code)}
              onChange={(e) => {
                handleFacet(facet.code, value.code, e.target.checked);
              }}
              id={value.code}
            />

            <div
              aria-hidden
              className="cursor-pointer flex items-center justify-center rounded-sm w-4 h-4 border peer-checked:border-blue peer-checked:bg-blue bg-white border-gray-3 peer-checked:[&>*]:!block"
            >
              <CheckMarkIcon2 className="hidden" />
            </div>

            <span className="flex-1 peer-checked:text-blue">{value.name}</span>

            <span className="peer-checked:text-white peer-checked:bg-blue bg-gray-2 inline-flex rounded-[30px] text-custom-xs px-2 ease-out duration-200 group-hover:text-white group-hover:bg-blue">
              {facetsCount?.[facet.id]?.[value.code] ?? 0}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default FacetDropdown;
